import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, distinctUntilChanged, filter, Observable, of, pipe, switchMap, tap } from 'rxjs';
import { selectUser } from '@workspace-app/state/global';
import { Bill } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { PermissionService } from '@workspace-app/shared/services/permission.service';
import {
  BillDataService,
  BillSourceEntityType,
  BillSummary,
} from '@workspace-src/app/crm/pages/billing/bill-data.service';
import { BillOverlayComponent } from '@workspace-src/app/crm/pages/billing/bill-view/bill-overlay.component';

const BILL_TYPE_LABELS: Record<string, string> = {
  DirectBill: 'Direct',
  GlBill: 'GL Collective',
  PowderDirectBill: 'Powder Direct',
  PowderGlBill: 'Powder GL',
  PretreatmentDirectBill: 'Pretreatment Direct',
  PretreatmentGlBill: 'Pretreatment GL',
};

const STATUS_SEVERITY: Record<string, 'info' | 'success' | 'warn' | 'danger' | 'secondary'> = {
  Draft: 'secondary',
  Issued: 'info',
  Paid: 'success',
  Cancelled: 'danger',
};

type FetchKey =
  | { kind: 'customer'; id: string }
  | { kind: 'source'; type: BillSourceEntityType; id: string }
  | null;

function fetchKeysEqual(a: FetchKey, b: FetchKey): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'customer' && b.kind === 'customer') return a.id === b.id;
  if (a.kind === 'source' && b.kind === 'source') return a.type === b.type && a.id === b.id;
  return false;
}

@Component({
  selector: 'app-bills-table',
  standalone: true,
  imports: [CommonModule, TableModule, TooltipModule, TagModule, FaIconComponent],
  templateUrl: './bills-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillsTableComponent {
  private billDataService = inject(BillDataService);
  private dialogService = inject(DialogService);
  private permissionService = inject(PermissionService);
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);

  customerId = input<string | null | undefined>();
  licenseId = input<string | null | undefined>();
  systemId = input<string | null | undefined>();
  systemType = input<Extract<BillSourceEntityType, 'CoatingSystem' | 'PretreatmentSystem'> | null | undefined>();

  protected bills = signal<BillSummary[]>([]);
  protected loading = signal(false);
  protected hasError = signal(false);

  private user = toSignal(this.store.select(selectUser));
  protected canRead: Signal<boolean> = computed(() => {
    const user = this.user();
    return !!user && this.permissionService.hasEffectivePermission(user, 'Billing.Read');
  });

  private overlayRef: DynamicDialogRef | null = null;

  // Single derived key collapses all fetch inputs (permission + 4 ids) into one value.
  // `rxMethod` + `distinctUntilChanged` then ensures we issue at most one HTTP call per
  // meaningful change — without this, the parent re-emitting its customer/license signal
  // (e.g. cache → server reconcile) and the user signal resolving asynchronously each
  // triggered a separate fetch.
  private fetchKey = computed<FetchKey>(() => {
    if (!this.canRead()) return null;
    const customerId = this.customerId();
    if (customerId) return { kind: 'customer', id: customerId };
    const licenseId = this.licenseId();
    if (licenseId) return { kind: 'source', type: 'License', id: licenseId };
    const systemId = this.systemId();
    const systemType = this.systemType();
    if (systemId && systemType) return { kind: 'source', type: systemType, id: systemId };
    return null;
  });

  constructor() {
    this.loadBills(toObservable(this.fetchKey));
  }

  private loadBills = rxMethod<FetchKey>(
    pipe(
      distinctUntilChanged(fetchKeysEqual),
      tap((key) => {
        if (!key) {
          this.bills.set([]);
          this.loading.set(false);
          this.hasError.set(false);
        }
      }),
      filter((key): key is NonNullable<FetchKey> => key !== null),
      tap(() => {
        this.loading.set(true);
        this.hasError.set(false);
      }),
      switchMap((key) => this.fetch(key)),
      tap((data) => {
        this.bills.set(data);
        this.loading.set(false);
      }),
    ),
  );

  private fetch(key: NonNullable<FetchKey>): Observable<BillSummary[]> {
    const source =
      key.kind === 'customer'
        ? this.billDataService.getBillsByCustomer(key.id)
        : this.billDataService.getBillsBySource(key.type, key.id);
    return source.pipe(
      catchError(() => {
        this.hasError.set(true);
        this.loading.set(false);
        return of([] as BillSummary[]);
      }),
    );
  }

  protected formatBillType(type: string): string {
    return BILL_TYPE_LABELS[type] ?? type;
  }

  protected statusSeverity(status: string): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    return STATUS_SEVERITY[status] ?? 'secondary';
  }

  protected viewBill(bill: BillSummary): void {
    // BillOverlayComponent re-fetches the full Bill via id; only id + completeBillNumber are read here.
    const partial = { id: bill.id, completeBillNumber: bill.completeBillNumber } as unknown as Bill;
    this.overlayRef = this.dialogService.open(BillOverlayComponent, {
      header: `Invoice ${bill.completeBillNumber}`,
      position: 'right',
      height: '100%',
      closable: true,
      draggable: false,
      maskStyleClass: 'mask',
      modal: true,
      baseZIndex: 1000,
      contentStyle: { overflow: 'auto' },
      data: { bill: partial },
    });
    this.overlayRef?.onClose.subscribe(() => {
      this.overlayRef = null;
      // Reload to reflect any status changes made inside the overlay.
      const key = this.fetchKey();
      if (!key) return;
      this.fetch(key)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((data) => this.bills.set(data));
    });
  }
}
